/**
 * Language Configuration for Multi-Language Support
 *
 * This file defines all supported languages and related configuration
 * for the translation system.
 *
 * ## Adding a New Language
 * 1. Add the language code to SupportedLanguage type
 * 2. Add entry to SUPPORTED_LANGUAGES array with code, name, flag, and locale
 * 3. Create `src/assets/i18n/{code}.json` with the translated keys
 * 4. Run `npm run i18n:translate -- --target={code}` to fill the gaps
 *
 * ## Language Codes
 * We use ISO 639-1 two-letter codes (en, fr, nl, de)
 *
 * @see https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
 */

/**
 * Supported language codes as a union type
 * Add new languages here when expanding support
 */
export type SupportedLanguage = 'en' | 'fr' | 'nl' | 'de';

/**
 * Language metadata interface
 */
export interface LanguageConfig {
  /** ISO 639-1 language code */
  code: SupportedLanguage;
  /** Native language name */
  name: string;
  /** English language name */
  nameEn: string;
  /** Flag emoji for display */
  flag: string;
  /** BCP 47 locale code for formatting (dates, numbers) */
  locale: string;
  /** Whether this language is RTL (right-to-left) */
  rtl: boolean;
}

/**
 * All supported languages with their configuration
 * Order determines display order in language selectors
 */
export const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  {
    code: 'fr',
    name: 'Français',
    nameEn: 'French',
    flag: '🇫🇷',
    locale: 'fr-BE',
    rtl: false
  },
  {
    code: 'en',
    name: 'English',
    nameEn: 'English',
    flag: '🇬🇧',
    locale: 'en-GB',
    rtl: false
  },
  {
    code: 'nl',
    name: 'Nederlands',
    nameEn: 'Dutch',
    flag: '\u{1F1F3}\u{1F1F1}',
    locale: 'nl-BE',
    rtl: false
  },
  {
    code: 'de',
    name: 'Deutsch',
    nameEn: 'German',
    flag: '🇩🇪',
    locale: 'de-DE',
    rtl: false
  }
];

/**
 * Default language when user has no preference set
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';

/**
 * Fallback language when a translation is missing
 * English is used as the source language for all translations
 */
export const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

/**
 * Translation domains — each domain corresponds to a feature area.
 *
 * Kept as a typed catalogue for tooling (the `scripts/i18n/*` authoring
 * helpers group keys by these top-level domains). The runtime loads the
 * whole `assets/i18n/{lang}.json` at once, so there is no per-domain lazy
 * loading anymore.
 */
export const TRANSLATION_DOMAINS = [
  'shared',       // Common UI elements (buttons, actions, validation)
  'auth',         // Login, register, password reset
  'dashboard',    // Main dashboard
  'properties',   // Property management
  'tenants',      // Tenant management
  'contracts',    // Contract management
  'loans',        // Loan management
  'maintenance',  // Maintenance tasks
  'expenses',     // Expense tracking
  'mail',         // Email/messaging
  'documents',    // Document management
  'admin',        // Admin panel
  'buildings',    // Building management
  'provider',     // Service provider workspace
  'workspace',    // Workspace settings
  'onboarding'    // Owner-workspace post-signup wizard
] as const;

export type TranslationDomain = typeof TRANSLATION_DOMAINS[number];

/**
 * Helper function to get language config by code
 */
export function getLanguageConfig(code: string): LanguageConfig | undefined {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
}

/**
 * Helper function to check if a language code is supported
 */
export function isLanguageSupported(code: string): code is SupportedLanguage {
  return SUPPORTED_LANGUAGES.some(lang => lang.code === code);
}

/**
 * Helper function to get the user's browser language if supported
 * Falls back to default language
 */
export function getBrowserLanguage(): SupportedLanguage {
  const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
  if (browserLang && isLanguageSupported(browserLang)) {
    return browserLang;
  }
  return DEFAULT_LANGUAGE;
}
